import { useEffect } from 'react';
import progressSignal from '../../utils/progressSignal';
import Skeleton from '../ui/Skeleton';
import AccountsLoadingSkeleton from '../accounts/AccountsLoadingSkeleton';

const SURFACE_CLASS_NAMES = {
  quiet: 'bg-[var(--color-bg)]',
  shell: 'bg-[var(--color-shell-bg)]',
  onboarding: 'bg-[var(--color-bg-secondary)]',
  public: 'bg-[var(--color-bg-secondary)]',
};

const MIN_HEIGHT_STYLES = {
  compact: 'clamp(96px, 18vh, 220px)',
  screen: '100vh',
};

const RoutePageHeaderSkeleton = ({ titleWidth = 'w-56', controlWidth = 'w-36' }) => (
  <div className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
    <div className="space-y-4">
      <Skeleton className={`h-10 ${titleWidth} rounded-full bg-[var(--color-surface-input)]/75`} />
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-24 rounded-xl bg-[var(--color-surface-input)]/70" />
        <Skeleton className="h-8 w-20 rounded-full bg-[var(--color-surface-input)]/55" />
        <Skeleton className="h-8 w-28 rounded-full bg-[var(--color-surface-input)]/55" />
      </div>
    </div>
    <Skeleton className={`h-11 ${controlWidth} rounded-xl bg-[var(--color-surface-input)]/70`} />
  </div>
);

const DashboardRouteSkeleton = () => (
  <div data-testid="route-skeleton-dashboard" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-56" controlWidth="w-40" />
    <Skeleton.ReviewBar />
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-6 shadow-[var(--shadow-surface)]">
      <div className="mb-6 flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-xl bg-[var(--color-surface-input)]/75" />
        <Skeleton className="h-7 w-52 rounded-full bg-[var(--color-surface-input)]/70" />
      </div>
      <Skeleton.TrendTileGrid count={4} />
    </section>
    <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
      <Skeleton.TrendChartCard titleWidth="w-44" chartHeight="h-[240px]" />
      <Skeleton.TrendChartCard titleWidth="w-40" chartHeight="h-[240px]" />
    </div>
  </div>
);

const SummaryCardGrid = ({ count = 3 }) => (
  <div
    data-testid="route-skeleton-summary-grid"
    className={`grid gap-4 ${count === 4 ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3'}`}
  >
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-5">
        <Skeleton className="h-4 w-28 rounded-full bg-[var(--color-surface-input)]/65" />
        <Skeleton className="mt-4 h-8 w-32 rounded-full bg-[var(--color-surface-input)]/75" />
      </div>
    ))}
  </div>
);

const TransactionsRouteSkeleton = () => (
  <div data-testid="route-skeleton-transactions" className="space-y-8">
    <RoutePageHeaderSkeleton titleWidth="w-64" controlWidth="w-44" />
    <div
      data-testid="route-skeleton-transaction-list-panel"
      className="rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-panel-from)] to-[var(--color-panel-to)] p-6 shadow-2xl"
    >
      <div className="min-h-[260px]">
        <Skeleton.TransactionsTable count={8} />
      </div>
    </div>
  </div>
);

const IncomeRouteSkeleton = () => (
  <div data-testid="route-skeleton-income" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-44" controlWidth="w-44" />
    <SummaryCardGrid />
    <Skeleton.TransactionsTable count={7} />
  </div>
);

const BudgetsRouteSkeleton = () => (
  <div data-testid="route-skeleton-budgets" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-48" controlWidth="w-40" />
    <SummaryCardGrid count={4} />
    <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
      <Skeleton.TrendChartCard titleWidth="w-40" chartHeight="h-[280px]" />
      <div className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-5">
        <Skeleton className="mb-5 h-6 w-44 rounded-full bg-[var(--color-surface-input)]/70" />
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-12 rounded-xl bg-[var(--color-surface-input)]/60" />
        ))}
      </div>
    </div>
  </div>
);

const CategoriesRouteSkeleton = () => (
  <div data-testid="route-skeleton-categories" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-56" controlWidth="w-44" />
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
      <div className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-5">
        {Array.from({ length: 7 }).map((_, index) => (
          <div key={index} className="flex items-center gap-4">
            <Skeleton className="h-10 w-10 rounded-xl bg-[var(--color-surface-input)]/70" />
            <Skeleton className={`h-5 ${index % 2 ? 'w-36' : 'w-48'} rounded-full bg-[var(--color-surface-input)]/65`} />
            <Skeleton className="ml-auto h-8 w-20 rounded-lg bg-[var(--color-surface-input)]/55" />
          </div>
        ))}
      </div>
      <div className="space-y-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-6">
        <Skeleton className="h-7 w-48 rounded-full bg-[var(--color-surface-input)]/75" />
        <Skeleton className="h-12 rounded-xl bg-[var(--color-surface-input)]/60" />
        <Skeleton className="h-36 rounded-xl bg-[var(--color-surface-input)]/55" />
        <Skeleton className="h-11 w-32 rounded-xl bg-[var(--color-surface-input)]/65" />
      </div>
    </div>
  </div>
);

const AccountsRouteSkeleton = () => (
  <div data-testid="route-skeleton-accounts" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-48" controlWidth="w-40" />
    <AccountsLoadingSkeleton />
  </div>
);

const HouseholdRouteSkeleton = () => (
  <div data-testid="route-skeleton-household" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-52" controlWidth="w-40" />
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
      <div className="space-y-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-6">
        <Skeleton className="h-7 w-44 rounded-full bg-[var(--color-surface-input)]/75" />
        <Skeleton className="h-24 rounded-xl bg-[var(--color-surface-input)]/60" />
        <Skeleton className="h-11 w-36 rounded-xl bg-[var(--color-surface-input)]/65" />
      </div>
      <div className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-6">
        <Skeleton className="mb-4 h-7 w-36 rounded-full bg-[var(--color-surface-input)]/75" />
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-xl bg-[var(--color-surface-input)]/60" />
        ))}
      </div>
    </div>
  </div>
);

const NetWorthRouteSkeleton = () => (
  <div data-testid="route-skeleton-net-worth" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-48" controlWidth="w-36" />
    <SummaryCardGrid />
    <Skeleton.TrendChartCard titleWidth="w-44" chartHeight="h-[300px]" />
    <Skeleton.TransactionsTable count={4} />
  </div>
);

const InsightsRouteSkeleton = () => (
  <div data-testid="route-skeleton-insights" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-44" controlWidth="w-36" />
    <Skeleton.TrendTileGrid count={4} />
    <div className="grid gap-6 xl:grid-cols-2">
      <Skeleton.TrendChartCard titleWidth="w-48" chartHeight="h-[270px]" />
      <Skeleton.TrendChartCard titleWidth="w-40" chartHeight="h-[270px]" />
    </div>
  </div>
);

const ReportsRouteSkeleton = () => (
  <div data-testid="route-skeleton-reports" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-44" controlWidth="w-36" />
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-6">
      <Skeleton className="h-7 w-52 rounded-full bg-[var(--color-surface-input)]/75" />
      <div className="mt-6 grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <Skeleton className="h-64 rounded-2xl bg-[var(--color-surface-input)]/60" />
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-12 rounded-xl bg-[var(--color-surface-input)]/55" />
          ))}
        </div>
      </div>
    </div>
    <div className="grid gap-4 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <Skeleton key={index} className="h-32 rounded-2xl bg-[var(--color-surface-row)]/70" />
      ))}
    </div>
  </div>
);

const LegalRouteSkeleton = () => (
  <div data-testid="route-skeleton-legal" className="space-y-6">
    <Skeleton className="h-10 w-64 max-w-full rounded-full bg-[var(--color-surface-input)]/75" />
    <Skeleton className="h-5 w-40 rounded-full bg-[var(--color-surface-input)]/55" />
    {Array.from({ length: 4 }).map((_, sectionIndex) => (
      <div key={sectionIndex} className="space-y-3">
        <Skeleton className="h-6 w-52 rounded-full bg-[var(--color-surface-input)]/70" />
        {Array.from({ length: 3 }).map((__, lineIndex) => (
          <Skeleton
            key={lineIndex}
            className={`h-4 rounded-full bg-[var(--color-surface-input)]/55 ${lineIndex === 2 ? 'w-4/5' : 'w-full'}`}
          />
        ))}
      </div>
    ))}
  </div>
);

const OnboardingRouteSkeleton = () => (
  <div data-testid="route-skeleton-onboarding" className="w-full max-w-xl space-y-5 rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)]/75 p-7 shadow-[var(--shadow-card)]">
    <Skeleton className="mx-auto h-7 w-48 rounded-full bg-[var(--color-surface-input)]/70" />
    <Skeleton className="mx-auto h-4 w-64 max-w-full rounded-full bg-[var(--color-surface-input)]/55" />
    <div className="grid gap-4 pt-3 sm:grid-cols-2">
      <Skeleton className="h-32 rounded-2xl bg-[var(--color-surface-input)]/60" />
      <Skeleton className="h-32 rounded-2xl bg-[var(--color-surface-input)]/60" />
    </div>
    <Skeleton className="mx-auto h-12 w-44 rounded-xl bg-[var(--color-surface-input)]/65" />
  </div>
);

const SettingsRouteSkeleton = () => (
  <div data-testid="route-skeleton-settings" className="space-y-7">
    <RoutePageHeaderSkeleton titleWidth="w-44" controlWidth="w-28" />
    <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
      <div className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-10 rounded-xl bg-[var(--color-surface-input)]/65" />
        ))}
      </div>
      <div className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface-row)]/70 p-6">
        <Skeleton className="h-7 w-56 rounded-full bg-[var(--color-surface-input)]/75" />
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-16 rounded-xl bg-[var(--color-surface-input)]/60" />
        ))}
      </div>
    </div>
  </div>
);

const GenericRouteSkeleton = () => (
  <div data-testid="route-skeleton-generic" className="space-y-7">
    <RoutePageHeaderSkeleton />
    <div className="grid gap-5 lg:grid-cols-2">
      <Skeleton.TrendChartCard titleWidth="w-48" chartHeight="h-[260px]" />
      <Skeleton.TrendChartCard titleWidth="w-44" chartHeight="h-[260px]" />
    </div>
    <Skeleton.TransactionsTable count={5} />
  </div>
);

const ROUTE_SKELETONS = {
  dashboard: DashboardRouteSkeleton,
  transactions: TransactionsRouteSkeleton,
  income: IncomeRouteSkeleton,
  budgets: BudgetsRouteSkeleton,
  categories: CategoriesRouteSkeleton,
  accounts: AccountsRouteSkeleton,
  household: HouseholdRouteSkeleton,
  settings: SettingsRouteSkeleton,
  'net-worth': NetWorthRouteSkeleton,
  insights: InsightsRouteSkeleton,
  reports: ReportsRouteSkeleton,
  legal: LegalRouteSkeleton,
  onboarding: OnboardingRouteSkeleton,
  generic: GenericRouteSkeleton,
};

export const ROUTE_SKELETON_KINDS = Object.freeze(Object.keys(ROUTE_SKELETONS));

const RouteContentSkeleton = ({ kind = 'generic' }) => {
  const SkeletonComponent = ROUTE_SKELETONS[kind] || ROUTE_SKELETONS.generic;
  return <SkeletonComponent />;
};

const RouteLoadingFallback = ({
  label = 'Loading page',
  surface = 'quiet',
  height = 'compact',
  routeKind = 'generic',
}) => {
  useEffect(() => {
    progressSignal.start();
    return () => progressSignal.done();
  }, []);

  const surfaceClassName = SURFACE_CLASS_NAMES[surface] || SURFACE_CLASS_NAMES.quiet;
  const minHeight = MIN_HEIGHT_STYLES[height] || MIN_HEIGHT_STYLES.compact;
  const showShellFrame = surface === 'shell' && height === 'screen';
  const showOnboardingFrame = surface === 'onboarding' && height === 'screen';
  const showPublicFrame = surface === 'public' && height === 'screen';

  return (
    <section
      role="status"
      aria-live="polite"
      aria-label={label}
      aria-busy="true"
      className={`w-full ${showShellFrame || showOnboardingFrame || showPublicFrame ? 'p-0' : 'px-4 py-6 sm:px-6'} ${surfaceClassName}`}
      style={{ minHeight }}
    >
      <span className="sr-only">{label}</span>
      {showShellFrame ? (
        <div
          data-testid="route-loading-shell-frame"
          className="flex min-h-screen w-full bg-[var(--color-shell-bg)]"
          aria-hidden="true"
        >
          <aside className="hidden w-[88px] shrink-0 border-r border-[var(--color-shell-divider)] bg-[var(--color-sidebar-bg)] md:block" />
          <main className="min-w-0 flex-1">
            <div className="h-16 border-b border-[var(--color-shell-divider)] bg-[var(--color-header-bg)]" />
            <div className="h-[3px] overflow-hidden bg-[var(--color-surface-hover)]">
              <div className="h-full w-1/3 rounded-r-full bg-gradient-to-r from-indigo-500 to-violet-400 opacity-90" />
            </div>
            <div className="mx-auto w-full max-w-[1180px] px-5 py-8 sm:px-8 lg:px-10">
              <RouteContentSkeleton kind={routeKind} />
            </div>
          </main>
        </div>
      ) : null}
      {showOnboardingFrame ? (
        <div
          data-testid="route-loading-onboarding-frame"
          className="flex min-h-screen w-full items-center justify-center bg-[linear-gradient(180deg,var(--color-bg-secondary)_0%,var(--color-bg)_100%)] px-6"
          aria-hidden="true"
        >
          <div className="flex w-full flex-col items-center gap-7">
            <div className="flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-[22px] shadow-[0_18px_48px_rgba(99,102,241,0.28)] ring-1 ring-indigo-400/35">
              <img src="/icons/apple-touch-icon.png" alt="" className="h-full w-full object-cover" draggable={false} />
            </div>
            <div className="flex items-center gap-2">
              {[0, 1, 2].map((index) => (
                <span
                  key={index}
                  className="h-2 w-2 rounded-full bg-[var(--color-accent)] motion-safe:animate-pulse"
                  style={{ animationDelay: `${index * 140}ms` }}
                />
              ))}
            </div>
            <OnboardingRouteSkeleton />
          </div>
        </div>
      ) : null}
      {showPublicFrame ? (
        <div
          data-testid="route-loading-public-frame"
          className="min-h-screen w-full bg-[linear-gradient(180deg,var(--color-bg-secondary)_0%,var(--color-bg)_100%)] px-5 py-12 sm:px-8"
          aria-hidden="true"
        >
          <div className="mx-auto mb-8 flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl shadow-[var(--shadow-surface)]">
            <img src="/icons/apple-touch-icon.png" alt="" className="h-full w-full object-cover" draggable={false} />
          </div>
          <div className="mx-auto max-w-3xl rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface-elevated)]/80 p-7 shadow-[var(--shadow-card)] sm:p-10">
            <RouteContentSkeleton kind={routeKind} />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default RouteLoadingFallback;
